import { useMemo, useState } from "react";
import {
  useGetProductsQuery,
  useToggleProductStatusMutation,
  useCreateProductMutation,
  useUpdateProductMutation,
} from "../../features/products/productApi";
import { useUploadImageMutation } from "../../features/images/imageApi";
import { useGetCategoriesQuery } from "../../features/categories/categoryApi";
import { useGetVendorsQuery } from "../../features/vendors/vendorApi";
import Table from "../../components/list/Table";
import Pagination from "../../components/list/Pagination";
import type { PaginationState } from "@tanstack/react-table";
import SlideInModal from "../../components/modals/SlideInModal";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormInput from "../../components/forms/FormInput";
import FormSelect from "../../components/forms/FormSelect";
import { FiDownload, FiEdit3, FiPlus, FiSearch, FiTag, FiTruck } from "react-icons/fi";
import type { Product } from "../../features/interfaces";
import GridLoader from "react-spinners/GridLoader";
import { toast } from "react-toastify";
import { exportToExcel, type ExportColumn } from "../../utils/excelExport";
import { stripHtml } from "../../utils/imageUtils";
import CreatableSelect from "react-select/creatable";
import VendorRemoteProductPicker from "./components/vendorRemoteMapping/VendorRemoteProductPicker";

type TagOption = { label: string; value: string };

const parseTags = (tags?: string | null): string[] => {
  if (!tags?.trim()) return [];
  return tags
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const tagsToString = (tags: TagOption[] | undefined): string =>
  (tags ?? []).map((tag) => tag.value.trim()).filter(Boolean).join(", ");

const tagsToOptions = (tags?: string | null): TagOption[] =>
  parseTags(tags).map((tag) => ({ label: tag, value: tag }));

const filterFormLabelClass = "text-sm font-semibold text-gray-800 mb-2 block";
const filterFormErrorClass = "text-rose-500 text-xs mt-1.5";
const filterFormInputClass =
  "w-full py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300";

const formSectionClass =
  "rounded-xl border border-gray-100 bg-gray-50/60 p-4 space-y-4";

const formSectionTitleClass =
  "text-xs font-bold uppercase tracking-wider text-[#8b7355]";

const tagSelectStyles = {
  control: (base: object) => ({
    ...base,
    borderColor: "#e5e7eb",
    borderRadius: "0.5rem",
    minHeight: "42px",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "&:hover": { borderColor: "#dbb685" },
  }),
  multiValue: (base: object) => ({
    ...base,
    backgroundColor: "#f5ebe0",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (base: object) => ({
    ...base,
    color: "#5c4a32",
    fontWeight: 600,
    fontSize: "0.75rem",
  }),
  multiValueRemove: (base: object) => ({
    ...base,
    color: "#8b7355",
    ":hover": { backgroundColor: "#dbb685", color: "white" },
  }),
};

const productSchema = yup.object({
  name: yup.string().required("Name is required"),
  description: yup.string().nullable().notRequired(),
  sku: yup.string().nullable().notRequired(),
  price: yup.number().required("Price is required").min(0, "Price must be positive"),
  cost: yup.number().nullable().notRequired().min(0, "Cost must be positive"),
  stock_quantity: yup.number().required("Stock quantity is required").min(0, "Stock must be positive"),
  category_id: yup
    .mixed<{ label: string; value: string } | string>()
    .required("Category is required")
    .test("is-valid-category", "Category is required", (value) => {
      if (!value) return false;
      return typeof value === 'string' || (typeof value === 'object' && value !== null && 'value' in value && (value as any).value);
    }),
  vendor_id: yup
    .mixed<{ label: string; value: string } | string>()
    .required("Vendor is required")
    .test("is-valid-vendor", "Vendor is required", (value) => {
      if (!value) return false;
      return typeof value === 'string' || (typeof value === 'object' && value !== null && 'value' in value && (value as any).value);
    }),
  tags: yup.array().of(
    yup.object({
      label: yup.string().required(),
      value: yup.string().required(),
    })
  ).optional(),
  vendor_remote_id: yup.string().nullable().notRequired(),
  vendor_remote_name: yup.string().nullable().notRequired(),
  vendor_remote_url: yup
    .string()
    .nullable()
    .notRequired()
    .test("is-url", "Enter a valid URL", (value) => {
      if (!value?.trim()) return true;
      try {
        new URL(value.trim());
        return true;
      } catch {
        return false;
      }
    }),
});

type ProductFormData = yup.InferType<typeof productSchema>;

const Products = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToToggle, setProductToToggle] = useState<Product | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const { data: productsResponse, isLoading, isFetching } =
    useGetProductsQuery();
  const { data: categoriesResponse } = useGetCategoriesQuery();
  const { data: vendorsResponse } = useGetVendorsQuery();
  const [createProduct, { isLoading: isCreating }] =
    useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] =
    useUpdateProductMutation();
  const [toggleStatus] = useToggleProductStatusMutation();
  const [uploadImageMutation] = useUploadImageMutation();

  const products = productsResponse?.data || [];

  const tagSuggestions = useMemo(() => {
    const unique = new Set<string>();
    products.forEach((product) => {
      parseTags(product.tags).forEach((tag) => unique.add(tag));
    });
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b))
      .map((tag) => ({ label: tag, value: tag }));
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = submittedSearch.trim().toLowerCase();
    return products.filter((p) => {
      if (selectedVendor && p.vendor_id !== selectedVendor) return false;
      if (!query) return true;
      const name = (p.name || "").toLowerCase();
      const sku = (p.sku || "").toLowerCase();
      const description = (p.description || "").toLowerCase();
      const tags = (p.tags || "").toLowerCase();
      return (
        name.includes(query) ||
        sku.includes(query) ||
        description.includes(query) ||
        tags.includes(query)
      );
    });
  }, [products, submittedSearch, selectedVendor]);

  const totalItems = filteredProducts.length;
  const categories = categoriesResponse?.data || [];
  const vendors = vendorsResponse?.data || [];

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const methods = useForm<ProductFormData>({
    resolver: yupResolver(productSchema) as any,
    defaultValues: {
      name: "",
      description: undefined,
      sku: undefined,
      price: 0,
      cost: undefined,
      stock_quantity: 0,
      category_id: undefined,
      vendor_id: undefined,
      tags: [],
      vendor_remote_id: undefined,
      vendor_remote_name: undefined,
      vendor_remote_url: undefined,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = methods;

  const watchedVendorId = useWatch({ control, name: "vendor_id" });
  const selectedFormVendorId =
    typeof watchedVendorId === "object" &&
    watchedVendorId !== null &&
    "value" in watchedVendorId
      ? (watchedVendorId as { value: string }).value
      : (watchedVendorId as string | undefined);

  const selectedFormVendor = useMemo(
    () => vendors.find((v) => v.vendor_id === selectedFormVendorId) || null,
    [vendors, selectedFormVendorId]
  );

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
      // Create preview from selected file
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image file using images API
  const uploadImage = async (file: File, title: string): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('title', title);

    try {
      const result = await uploadImageMutation(formData).unwrap();
      // The images API returns { message, data: { image_id, title, image_link, ... } }
      return result.data?.image_link || '';
    } catch (error) {
      console.error("Upload error:", error);
      throw new Error('Failed to upload image');
    }
  };

  const categoryOptions = categories.map((cat) => ({
    label: cat.name,
    value: cat.category_id || "",
  }));

  const vendorOptions = vendors.map((vendor) => ({
    label: vendor.name,
    value: vendor.vendor_id || "",
  }));

  const handleOpenModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      const categoryOption = categoryOptions.find(opt => opt.value === product.category_id);
      const vendorOption = vendorOptions.find(opt => opt.value === product.vendor_id);

      reset({
        name: product.name,
        description: product.description || undefined,
        sku: product.sku || undefined,
        price: product.price,
        cost: product.cost || undefined,
        stock_quantity: product.stock_quantity,
        category_id: categoryOption || undefined,
        vendor_id: vendorOption || undefined,
        tags: tagsToOptions(product.tags),
        vendor_remote_id: product.vendor_remote_id || undefined,
        vendor_remote_name: product.vendor_remote_name || undefined,
        vendor_remote_url: product.vendor_remote_url || undefined,
      });
      setImagePreview(product.image_url || null);
      setSelectedFile(null);
    } else {
      setEditingProduct(null);
      reset({
        name: "",
        description: undefined,
        sku: undefined,
        price: 0,
        cost: undefined,
        stock_quantity: 0,
        category_id: undefined,
        vendor_id: undefined,
        tags: [],
        vendor_remote_id: undefined,
        vendor_remote_name: undefined,
        vendor_remote_url: undefined,
      });
      setImagePreview(null);
      setSelectedFile(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setImagePreview(null);
    setSelectedFile(null);
    reset();
  };

  const onSubmit = async (data: ProductFormData) => {
    try {
      setIsUploading(true);

      // Upload image if a new file was selected
      let imageUrl: string | undefined = undefined;
      if (selectedFile) {
        try {
          // Use product name as the image title
          const imageTitle = data.name || `Product Image - ${editingProduct?.product_id || 'New'}`;
          imageUrl = await uploadImage(selectedFile, imageTitle);
        } catch (error) {
          console.error("Error uploading image:", error);
          toast.error("Failed to upload image. Please try again.");
          setIsUploading(false);
          return;
        }
      } else if (editingProduct?.image_url) {
        // Keep existing image if no new file is selected
        imageUrl = editingProduct.image_url;
      }

      // Extract value from category_id select object if it's an object
      const categoryId = typeof data.category_id === 'object' && data.category_id !== null && 'value' in data.category_id
        ? (data.category_id as { label: string; value: string }).value
        : (data.category_id as string);

      // Extract value from vendor_id select object if it's an object
      const vendorId = typeof data.vendor_id === 'object' && data.vendor_id !== null && 'value' in data.vendor_id
        ? (data.vendor_id as { label: string; value: string }).value
        : (data.vendor_id as string);

      const productData: Partial<Product> = {
        name: data.name,
        description: data.description || undefined,
        sku: data.sku || undefined,
        image_url: imageUrl || undefined,
        price: data.price,
        cost: data.cost || undefined,
        stock_quantity: data.stock_quantity,
        category_id: categoryId,
        vendor_id: vendorId,
        tags: tagsToString(data.tags as TagOption[] | undefined),
        vendor_remote_id: data.vendor_remote_id?.trim() || undefined,
        vendor_remote_name: data.vendor_remote_name?.trim() || undefined,
        vendor_remote_url: data.vendor_remote_url?.trim() || undefined,
        is_active: true,
      };

      if (editingProduct?.product_id) {
        await updateProduct({
          id: editingProduct.product_id,
          data: productData,
        }).unwrap();
        toast.success("Product updated successfully");
      } else {
        await createProduct(productData).unwrap();
        toast.success("Product created successfully");
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleToggleClick = (product: Product) => {
    setProductToToggle(product);
    setShowConfirmModal(true);
  };

  const submitSearch = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSubmittedSearch(searchInput);
  };

  const renderTextCell = (info: any) => (
    <span className="text-gray-600 font-medium">
      {info.getValue() || <span className="text-gray-400">-</span>}
    </span>
  );

  const renderProductCell = (info: any) => {
    const product = info.row.original as Product;
    const vendorName = vendors.find(
      (v) => v.vendor_id === product.vendor_id
    )?.name;
    return (
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-50">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              N/A
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-800">{product.name}</p>
          {vendorName && (
            <span className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600">
              <FiTruck size={10} className="shrink-0 opacity-70" />
              {vendorName}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderTagsCell = (info: any) => {
    const tags = parseTags(info.getValue());
    if (tags.length === 0) {
      return <span className="text-xs text-gray-400 italic">No tags</span>;
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full border border-[#e8d5b8] bg-[#faf6f0] px-2.5 py-0.5 text-xs font-medium text-[#6b5438]"
          >
            <FiTag size={10} className="shrink-0 opacity-70" />
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const renderPriceCell = (info: any) => {
    const value = info.getValue();
    return (
      <span className="text-gray-600 font-medium">
        {value ?? <span className="text-gray-400">-</span>}
      </span>
    );
  };

  const renderCategoryCell = (info: any) => {
    const category = categories.find((c) => c.category_id === info.getValue());
    return <span className="text-gray-600 font-medium">{category?.name || "-"}</span>;
  };

  const renderStatusCell = (info: any) => {
    const isActive = info.getValue();
    const product = info.row.original as Product;
    return (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => handleToggleClick(product)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:ring-offset-2 ${
            isActive ? "bg-[#dbb685]" : "bg-gray-300"
          }`}
          role="switch"
          aria-checked={isActive}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isActive ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    );
  };

  const renderActions = ({ row }: any) => {
    const product = row.original as Product;
    return (
      <div className="flex justify-center items-center gap-1.5">
        <button
          type="button"
          className="px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-blue-600 hover:bg-blue-50 cursor-pointer border border-blue-200 bg-blue-50/50"
          onClick={() => handleOpenModal(product)}
          title="Edit"
        >
          <FiEdit3 size={14} />
          <span className="text-xs font-medium">Edit</span>
        </button>
      </div>
    );
  };

  const columns = [
    {
      id: "product_id",
      header: "ID",
      accessorKey: "product_id",
      cell: renderTextCell,
      size: 60,
      meta: { align: "left" },
    },
    {
      id: "name",
      header: "Product",
      accessorKey: "name",
      cell: renderProductCell,
      size: 240,
      meta: { align: "left" },
    },
    {
      id: "tags",
      header: "Tags",
      accessorKey: "tags",
      cell: renderTagsCell,
      size: 430,
      meta: { align: "left" },
    },
    {
      id: "price",
      header: "Price",
      accessorKey: "price",
      cell: renderPriceCell,
      size: 100,
      meta: { align: "right" },
    },
    {
      id: "category_id",
      header: "Category",
      accessorKey: "category_id",
      cell: renderCategoryCell,
      size: 120,
      meta: { align: "left" },
    },
    {
      id: "is_active",
      header: "Status",
      accessorKey: "is_active",
      cell: renderStatusCell,
      size: 70,
      meta: { align: "center" },
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "actions",
      cell: renderActions,
      size: 120,
      meta: { align: "center" },
    },
  ];

  const handleConfirmToggle = async () => {
    if (productToToggle?.product_id) {
      try {
        await toggleStatus({
          id: productToToggle.product_id,
          is_active: !productToToggle.is_active,
        }).unwrap();
        toast.success(`Product ${!productToToggle.is_active ? 'activated' : 'deactivated'} successfully`);
        setShowConfirmModal(false);
        setProductToToggle(null);
      } catch (error) {
        console.error("Error toggling status:", error);
        setShowConfirmModal(false);
        setProductToToggle(null);
      }
    }
  };

  const getCategoryName = (categoryId: string | undefined) =>
    categories.find((c) => c.category_id === categoryId)?.name ?? "";

  const getVendorName = (vendorId: string | undefined) =>
    vendors.find((v) => v.vendor_id === vendorId)?.name ?? "";

  const handleExport = () => {
    const dataToExport = filteredProducts;
    if (dataToExport.length === 0) {
      toast.info("No products to export");
      return;
    }

    setIsExporting(true);
    try {
      const exportColumns: ExportColumn[] = [
        { key: "product_id", header: "Product ID" },
        { key: "name", header: "Name" },
        {
          key: "description",
          header: "Description",
          accessor: (row: Product) => stripHtml(row.description),
        },
        { key: "sku", header: "Vendor SKU" },
        { key: "tags", header: "Tags" },
        { key: "image_url", header: "Image URL" },
        {
          key: "price",
          header: "Price",
          accessor: (row: Product) =>
            row.price != null ? Number(row.price) : "",
        },
        {
          key: "cost",
          header: "Cost",
          accessor: (row: Product) =>
            row.cost != null ? Number(row.cost) : "",
        },
        {
          key: "stock_quantity",
          header: "Stock Quantity",
          accessor: (row: Product) =>
            row.stock_quantity != null ? Number(row.stock_quantity) : "",
        },
        { key: "category_id", header: "Category ID" },
        {
          key: "category_id",
          header: "Category",
          accessor: (row: Product) => getCategoryName(row.category_id),
        },
        { key: "vendor_id", header: "Vendor ID" },
        {
          key: "vendor_id",
          header: "Vendor",
          accessor: (row: Product) => getVendorName(row.vendor_id),
        },
        {
          key: "is_active",
          header: "Status",
          accessor: (row: Product) => (row.is_active ? "Active" : "Inactive"),
        },
      ];

      exportToExcel(dataToExport, exportColumns, "products");
      toast.success(`Exported ${dataToExport.length} products successfully`);
    } catch (error: any) {
      console.error("Error exporting products:", error);
      toast.error(error?.message || "Failed to export products");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-800 mb-1">Products</h1>
            <p className="text-sm text-gray-500">
              Manage catalog items, tags, and availability
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting || isLoading}
              className="px-4 py-2 text-sm font-medium text-green-600 border border-green-600 rounded-lg hover:bg-green-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ minHeight: "38px" }}
            >
              <FiDownload size={16} />
              {isExporting ? "Exporting..." : "Export"}
            </button>
            <button
              type="button"
              onClick={() => handleOpenModal()}
              className="px-4 py-2 text-white rounded-lg font-semibold flex items-center justify-center gap-2 text-sm"
              style={{
                minWidth: "130px",
                minHeight: "38px",
                backgroundColor: "#dbb685",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#c9a570";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#dbb685";
              }}
            >
              <FiPlus size={16} />
              <span>Add Product</span>
            </button>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between gap-3">
          <select
            value={selectedVendor}
            onChange={(e) => {
              setSelectedVendor(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            className="py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300 min-w-[200px]"
          >
            <option value="">All vendors</option>
            {vendorOptions.map((vendor) => (
              <option key={vendor.value} value={vendor.value}>
                {vendor.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitSearch();
                }
              }}
              placeholder="Search by name, SKU, description, or tags"
              className="w-80 max-w-full py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300"
            />
            <button
              type="button"
              onClick={submitSearch}
              className="px-4 py-2.5 text-sm font-semibold text-white rounded-lg flex items-center gap-2"
              style={{ backgroundColor: "#dbb685" }}
            >
              <FiSearch size={16} />
              Search
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <GridLoader color="#dbb685" />
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={filteredProducts}
                isSearchable={false}
                isSortable={true}
                isFetching={isFetching}
                pagination={true}
                paginationState={pagination}
                setPaginationState={setPagination}
                tableClass="w-full table-fixed"
                thClass="text-gray-500 font-semibold text-xs uppercase tracking-wide py-2.5 px-4 whitespace-nowrap"
                tdClass="text-sm py-2 px-4 text-gray-700 font-medium"
                theadRowClass="bg-gray-50 border-b border-gray-200"
              />
              <div className="mt-6 flex justify-between items-center bg-white rounded-lg border border-gray-200 p-4 mx-4 mb-4">
                <div className="text-sm text-gray-600">
                  Showing{" "}
                  <span className="font-semibold text-gray-800">
                    {totalItems === 0
                      ? 0
                      : pagination.pageIndex * pagination.pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-800">
                    {Math.min(
                      (pagination.pageIndex + 1) * pagination.pageSize,
                      totalItems
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-800">{totalItems}</span>{" "}
                  products
                </div>
                <Pagination
                  currentPage={pagination.pageIndex + 1}
                  totalItems={totalItems}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={(p: number) => {
                    setPagination({ ...pagination, pageIndex: p - 1 });
                  }}
                  btnClass="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                  pageNumClass="text-sm px-3 py-1"
                  activePageNumClass="text-white rounded-lg font-semibold"
                  activePageNumStyle={{ backgroundColor: "#dbb685" }}
                  inactivePageNumClass="hover:bg-gray-100 rounded-lg text-gray-700 font-medium"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <SlideInModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProduct ? "Edit Product" : "Add Product"}
        width="760px"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <section className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Basic details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Name"
                  name="name"
                  placeholder="Enter product name"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
                <FormInput
                  label="Vendor SKU"
                  name="sku"
                  placeholder="Enter vendor SKU"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
              </div>
              <FormInput
                label="Description"
                name="description"
                type="textarea"
                placeholder="Enter description"
                register={register}
                errors={errors}
                labelClassName={filterFormLabelClass}
                className={filterFormInputClass}
                errorClassName={filterFormErrorClass}
              />
            </section>

            <section className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Image</h3>
              <div className="rounded-lg border-2 border-dashed border-gray-200 bg-white p-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-[#dbb685] file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-[#c9a570]"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Max 5MB. JPG, PNG, GIF, or WebP.
                </p>
                {imagePreview && (
                  <div className="mt-4 flex items-center gap-4 rounded-lg border border-gray-100 bg-gray-50 p-3">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="h-20 w-20 rounded-lg border border-gray-200 object-cover"
                      onError={() => {
                        setImagePreview(null);
                        setSelectedFile(null);
                      }}
                    />
                    <div className="min-w-0 flex-1 text-sm text-gray-600">
                      {selectedFile ? (
                        <>
                          <p className="truncate font-medium">{selectedFile.name}</p>
                          <p>{(selectedFile.size / 1024).toFixed(2)} KB</p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium">Current image</p>
                          <button
                            type="button"
                            onClick={() => {
                              setImagePreview(null);
                              setSelectedFile(null);
                            }}
                            className="mt-1 text-sm font-medium text-rose-600 hover:text-rose-700"
                          >
                            Remove image
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Pricing & inventory</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormInput
                  label="Price"
                  name="price"
                  type="number"
                  placeholder="0.00"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
                <FormInput
                  label="Cost"
                  name="cost"
                  type="number"
                  placeholder="0.00"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
                <FormInput
                  label="Stock Quantity"
                  name="stock_quantity"
                  type="number"
                  placeholder="0"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
              </div>
            </section>

            <section className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Organization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormSelect
                  label="Category"
                  name="category_id"
                  placeholder="Select category"
                  control={control}
                  options={categoryOptions}
                  labelClassName={filterFormLabelClass}
                  className="border border-gray-200 rounded-lg text-sm shadow-sm"
                  borderColor="white"
                  errorClassName={filterFormErrorClass}
                />
                <FormSelect
                  label="Vendor"
                  name="vendor_id"
                  placeholder="Select vendor"
                  control={control}
                  options={vendorOptions}
                  labelClassName={filterFormLabelClass}
                  className="border border-gray-200 rounded-lg text-sm shadow-sm"
                  borderColor="white"
                  errorClassName={filterFormErrorClass}
                />
              </div>
            </section>

            <section className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Vendor product mapping</h3>
              <p className="text-xs text-gray-500 -mt-2">
                Optional link to this product in the vendor&apos;s own system (Shopify, WooCommerce, etc.).
              </p>

              <VendorRemoteProductPicker
                vendor={selectedFormVendor}
                onSelect={(selection) => {
                  setValue("vendor_remote_id", selection.vendor_remote_id, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue("vendor_remote_name", selection.vendor_remote_name, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue("vendor_remote_url", selection.vendor_remote_url, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormInput
                  label="Vendor product ID"
                  name="vendor_remote_id"
                  placeholder="e.g. Shopify variant ID"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
                <FormInput
                  label="Vendor product name"
                  name="vendor_remote_name"
                  placeholder="Name in vendor system"
                  register={register}
                  errors={errors}
                  labelClassName={filterFormLabelClass}
                  className={filterFormInputClass}
                  errorClassName={filterFormErrorClass}
                />
              </div>
              <FormInput
                label="Vendor product URL"
                name="vendor_remote_url"
                placeholder="https://..."
                register={register}
                errors={errors}
                labelClassName={filterFormLabelClass}
                className={filterFormInputClass}
                errorClassName={filterFormErrorClass}
              />
            </section>

            <section className={formSectionClass}>
              <h3 className={formSectionTitleClass}>Tags</h3>
              <div>
                <label className={filterFormLabelClass}>Product tags</label>
                <p className="mb-2 text-xs text-gray-500">
                  Type a tag and press Enter. Select existing tags or create new ones.
                </p>
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      isMulti
                      isClearable
                      placeholder="Add tags..."
                      options={tagSuggestions}
                      value={field.value as TagOption[]}
                      onChange={(selected) => field.onChange(selected ?? [])}
                      onBlur={field.onBlur}
                      formatCreateLabel={(input) => `Add "${input}"`}
                      styles={tagSelectStyles}
                      className="text-sm"
                    />
                  )}
                />
              </div>
            </section>

            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-lg shadow-sm flex items-center justify-center cursor-pointer"
                style={{
                  minWidth: "100px",
                  minHeight: "40px",
                  border: "2px solid #d1d5db",
                  transition: "background-color 0.2s ease, border-color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating || isUpdating || isUploading}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{
                  minWidth: "140px",
                  minHeight: "40px",
                  backgroundColor: "#dbb685",
                  transition: "background-color 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isCreating && !isUpdating && !isUploading) {
                    e.currentTarget.style.backgroundColor = "#c9a570";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCreating && !isUpdating && !isUploading) {
                    e.currentTarget.style.backgroundColor = "#dbb685";
                  }
                }}
              >
                {isUploading
                  ? "Uploading..."
                  : isCreating || isUpdating
                    ? "Saving..."
                    : editingProduct
                      ? "Update"
                      : "Create"}
              </button>
            </div>
          </form>
        </FormProvider>
      </SlideInModal>

      {/* Confirmation Modal for Toggle Status */}
      {showConfirmModal && productToToggle && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={() => {
              setShowConfirmModal(false);
              setProductToToggle(null);
            }}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {productToToggle.is_active
                  ? "Deactivate Product"
                  : "Activate Product"}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to{" "}
                {productToToggle.is_active ? "deactivate" : "activate"} the
                product{" "}
                <span className="font-semibold text-gray-900">
                  "{productToToggle.name}"
                </span>
                ?
                {productToToggle.is_active
                  ? " This product will no longer be available for selection."
                  : " This product will become available for selection."}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setProductToToggle(null);
                  }}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors"
                  style={{ minWidth: "100px", minHeight: "40px" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.borderColor = "#9ca3af";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmToggle}
                  className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors"
                  style={{
                    minWidth: "120px",
                    minHeight: "40px",
                    backgroundColor: productToToggle.is_active
                      ? "#dc2626"
                      : "#10b981",
                    transition: "background-color 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      productToToggle.is_active ? "#b91c1c" : "#059669";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      productToToggle.is_active ? "#dc2626" : "#10b981";
                  }}
                >
                  {productToToggle.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Products;
